/**
 * Query keys do domínio agenda (apps/crm — página /agenda e grades semanais).
 *
 * As três fontes (bookings, bloqueios de quadra, aulas) são consultadas por
 * RANGE de dias (`from`/`to`, 'yyyy-MM-dd') desde a visão semana — o modo dia
 * usa `from === to`. Invalidation por prefixo (`agenda.bookingsAll` etc.)
 * cobre qualquer range em cache de uma vez (React Query v5 partial match).
 */
import { tuple } from "./core";
import type { StudentViewScope } from "./students";

const root = ["agenda"] as const;

type ScopeInput = StudentViewScope | { type: StudentViewScope } | null | undefined;

const bookingsRoot = tuple(root, "bookings");
const blockedRoot = tuple(root, "blocked");
const lessonsRoot = tuple(root, "lessons");
const originsRoot = tuple(root, "aggregator-origins");
const lessonStudentsRoot = tuple(root, "lesson-students");

export const agenda = {
  all: root,
  /** Prefixo p/ invalidar toda reserva em qualquer range/escopo. */
  bookingsAll: bookingsRoot,
  bookings: (
    viewScope: ScopeInput,
    clubIds: readonly string[] | null | undefined,
    from: string | null | undefined,
    to: string | null | undefined,
    courtIds: readonly string[] | null | undefined,
  ) => tuple(bookingsRoot, viewScope, clubIds, from, to, courtIds),
  /** Prefixo p/ invalidar bloqueios de quadra em qualquer range/escopo. */
  blockedAll: blockedRoot,
  blocked: (
    viewScope: ScopeInput,
    clubIds: readonly string[] | null | undefined,
    from: string | null | undefined,
    to: string | null | undefined,
    courtIds: readonly string[] | null | undefined,
  ) => tuple(blockedRoot, viewScope, clubIds, from, to, courtIds),
  /** Prefixo p/ invalidar as aulas da agenda em qualquer range/escopo. */
  lessonsAll: lessonsRoot,
  lessons: (
    viewScope: ScopeInput,
    clubIds: readonly string[] | null | undefined,
    from: string | null | undefined,
    to: string | null | undefined,
  ) => tuple(lessonsRoot, viewScope, clubIds, from, to),
  /**
   * Origem de agregador (Wellhub/TotalPass) das reservas visíveis.
   *
   * Chaveada pelos IDS das reservas, e não pelo range: a consulta é derivada
   * do resultado de `bookings`, e usar o range faria a lista voltar do cache
   * desatualizada quando a de reservas mudasse por outro motivo.
   */
  aggregatorOriginsAll: originsRoot,
  aggregatorOrigins: (bookingIds: readonly string[] | null | undefined) =>
    tuple(originsRoot, bookingIds),
  /** Prefixo p/ invalidar a lista nominal de alunos de qualquer conjunto de aulas. */
  lessonStudentsAll: lessonStudentsRoot,
  /**
   * Nomes dos alunos das aulas visíveis (grade por quadra, D-A4).
   *
   * Chaveada pelos IDS das aulas, e não pelo range: a consulta é derivada do
   * resultado de `lessons` — mesmo critério de `aggregatorOrigins`.
   */
  lessonStudents: (lessonIds: readonly string[] | null | undefined) =>
    tuple(lessonStudentsRoot, lessonIds),
} as const;
